import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TaskAuditAction } from '../../data/taskCaseDetails';
import * as auditService from '../../services/caseRequestAuditLogs';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';

type Props = {
  caseRequestId: string;
};

type Row = {
  date: string;
  time: string;
  actor: string;
  role: string;
  action: TaskAuditAction;
  actionLabel: string;
};

function actionFromApi(action: string, actionDisplayName: string): TaskAuditAction {
  const s = `${action} ${actionDisplayName}`.trim().toLowerCase();
  if (s.includes('forward')) return 'fwd';
  if (s.includes('create') || s.includes('submitted') || s.includes('submit')) return 'cre';
  if (s.includes('view') || s.includes('download') || s.includes('preview')) return 'view';
  return 'upd';
}

function roleLine(e: auditService.CaseRequestAuditLogEntry): string {
  const role = e.userRole.trim();
  return role || '—';
}

function auditClass(a: TaskAuditAction): string {
  if (a === 'upd') return 'au-upd';
  if (a === 'view') return 'au-view';
  if (a === 'fwd') return 'au-fwd';
  return 'au-cre';
}

function mapApiEntry(e: auditService.CaseRequestAuditLogEntry): Row {
  const dt = formatDisplayDateTime(e.createdAt);
  const [date, time] = dt.includes(',') ? dt.split(',').map((s) => s.trim()) : [dt, ''];
  const actor = e.userDisplayName.trim() || 'System';
  const action = actionFromApi(e.action, e.actionDisplayName);
  return {
    date: date || '—',
    time: time || '—',
    actor,
    role: roleLine(e),
    action,
    actionLabel: e.actionDisplayName.trim() || e.action.trim() || 'Activity',
  };
}

function sortRows(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => {
    const ka = `${a.date} ${a.time}`.trim();
    const kb = `${b.date} ${b.time}`.trim();
    return kb.localeCompare(ka);
  });
}

export default function TaskCaseAuditLogTab({ caseRequestId }: Props) {
  const canLoad = isCaseRequestUuid(caseRequestId);
  const [loading, setLoading] = useState(canLoad);
  const [loadError, setLoadError] = useState('');
  const [rows, setRows] = useState<Row[]>([]);

  const load = useCallback(async () => {
    if (!canLoad) {
      setLoading(false);
      setLoadError('Case request id is missing or invalid.');
      return;
    }

    setLoading(true);
    setLoadError('');
    const r = await auditService.getCaseRequestAuditLogs(caseRequestId);
    setLoading(false);
    if (!r.success) {
      const msg = r.message ?? 'Failed to load audit logs.';
      setLoadError(msg);
      setFlashToast({ message: msg, variant: 'error' });
      return;
    }
    const list = (r.data.entries ?? []).map(mapApiEntry);
    setRows(sortRows(list));
  }, [canLoad, caseRequestId]);

  useEffect(() => {
    void load();
  }, [load]);

  const emptyLabel = useMemo(
    () => 'No audit log activity found for this case request.',
    [],
  );

  if (!canLoad) {
    return <p className="tcd-doc-empty-msg">Case request id is missing or invalid.</p>;
  }

  if (loading) {
    return <p className="tcd-loading">Loading audit logs…</p>;
  }

  if (loadError) {
    return <p className="tcd-error" role="alert">{loadError}</p>;
  }

  return (
    <>
      <div className="log-title">Audit Log Details</div>
      {rows.length ? (
        <table className="aud-tbl">
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>Activity By</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.date}-${r.time}-${r.actor}-${r.actionLabel}-${i}`}>
                <td>
                  <div className="dt-main">{r.date}</div>
                  <div className="dt-sub">{r.time}</div>
                </td>
                <td>
                  <div className="au-row">
                    <div className="au-ico">
                      {r.actor === 'System' ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden>
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden>
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="au-name">{r.actor}</div>
                      <div className="au-role">{r.role}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`au-pill ${auditClass(r.action)}`}>{r.actionLabel}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="tcd-doc-empty-msg">{emptyLabel}</p>
      )}
    </>
  );
}
